import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import { X } from "lucide-react";

import { cn } from "../../lib/utils";

export type DialogV2Props = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  paperClassName?: string;
  contentClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  backdropClassName?: string;
  showCloseButton?: boolean;
  maxWidth?: false | "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
  fullScreen?: boolean;
};

const paperSx = {
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "0 24px 80px rgba(15, 23, 42, 0.2)",
};

export const DialogV2: React.FC<DialogV2Props> = ({
  open,
  onClose,
  title,
  description,
  children,
  actions,
  className,
  paperClassName,
  contentClassName,
  titleClassName,
  descriptionClassName,
  backdropClassName,
  showCloseButton = true,
  maxWidth = "sm",
  fullWidth = true,
  fullScreen = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          onClose();
        }
      }}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      className={className}
      slotProps={{
        backdrop: {
          className: cn("bg-black/20", backdropClassName),
        },
        paper: {
          className: cn("relative", paperClassName),
          sx: paperSx,
        },
      }}
    >
      <Box className="relative">
        {showCloseButton ? (
          <IconButton
            aria-label="Close dialog"
            onClick={onClose}
            className="!absolute right-4 top-4 z-10"
            size="small"
          >
            <X className="h-4 w-4" />
          </IconButton>
        ) : null}

        {(title || description) ? (
          <DialogTitle className={cn("px-6 pt-6 pb-0", titleClassName)}>
            {title ? <div className="text-lg font-semibold leading-6">{title}</div> : null}
            {description ? (
              <div className={cn("mt-2 text-sm text-gray-500", descriptionClassName)}>
                {description}
              </div>
            ) : null}
          </DialogTitle>
        ) : null}

        <DialogContent className={cn("px-0 pb-0 pt-0", contentClassName)}>
          {children}
        </DialogContent>

        {actions ? <div className="px-5 pb-5 pt-2">{actions}</div> : null}
      </Box>
    </Dialog>
  );
};

export default DialogV2;
